import { ReactNode, useEffect } from 'react';
import { useAuthStore } from '@/entities/auth';
import { SessionExpiryDialog } from '@/features/session-expiry';
import { TemporaryPasswordModal } from '@/features/temporary-password-modal';
import { AuthPage } from '@/pages/AuthPage';
import { registerUnauthorizedHandler } from '@/shared/api';
import { useAuth, useUiSettingsSync } from '@/shared/hooks';
import { Spinner } from '@ibsys/kit';

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider component
 *
 * Wraps the application and handles authentication checking when the application loads.
 * Uses the authentication store to check if the user is authenticated and get user data.
 */
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { isLoading } = useAuthStore();
  const { checkAuth, isAuthenticated, user } = useAuth();

  // Реакция на отказ бэка по неавторизованности. Регистрируется раньше проверки сессии ниже:
  // эффекты выполняются в порядке объявления, поэтому обработчик стоит уже к первому запросу
  // приложения. Снятие при размонтировании не оставляет обработчик от прежнего дерева
  useEffect(
    () =>
      registerUnauthorizedHandler(() => {
        useAuthStore.getState().expireSession();
      }),
    [],
  );

  // Синхронизация настроек интерфейса с бэкендом (дебаунс 500 мс)
  const userId = user?.id != null ? String(user.id) : null;
  useUiSettingsSync(userId);

  // Check authentication status when the component mounts
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  if (isLoading) {
    return <Spinner className={'fixed top-1/2 left-1/2 -translate-1/2 size-9'} />;
  }

  if (!isAuthenticated) {
    return <AuthPage />;
  }

  return (
    <>
      {children}
      <TemporaryPasswordModal />
      <SessionExpiryDialog />
    </>
  );
};
